import { useState, useRef, useCallback } from "react";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

function getUserId() {
  let id = localStorage.getItem("senticlabs_uid");
  if (!id) {
    id = "u_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("senticlabs_uid", id);
  }
  return id;
}

const STYLES = [
  { id: "70s_yearbook", emoji: "📸", label: "70s Yearbook", desc: "Groovy vintage vibes" },
  { id: "renaissance", emoji: "🎨", label: "Renaissance", desc: "Old master painting" },
  { id: "anime", emoji: "✨", label: "Anime Hero", desc: "Japanese animation" },
  { id: "80s_neon", emoji: "🌆", label: "80s Neon", desc: "Synthwave cyberpunk" },
  { id: "oil_painting", emoji: "🖼️", label: "Oil Portrait", desc: "Classic fine art" },
  { id: "comic_book", emoji: "💥", label: "Comic Book", desc: "Marvel energy" },
  { id: "watercolor", emoji: "🌊", label: "Watercolor", desc: "Soft dreamy strokes" },
  { id: "pixar", emoji: "🎬", label: "Pixar Character", desc: "Animated movie magic" },
];

export default function App() {
  const [screen, setScreen] = useState("home");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [resultImageUrl, setResultImageUrl] = useState(null);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();
  const userId = getUserId();

  const handleFile = useCallback((file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setScreen("pick");
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleTransform = async () => {
    if (!selectedStyle) return;
    setScreen("processing");
    setError(null);
    try {
      const formData = new FormData();
      formData.append("image", selectedFile);
      formData.append("style", selectedStyle);
      formData.append("userId", userId);
      const res = await fetch(API_URL + "/transform", { method: "POST", body: formData });
      const data = await res.json();
      if (res.status === 402) { setScreen("paywall"); return; }
      if (!res.ok) throw new Error(data.message || "Transform failed");
      setResultImageUrl(data.imageUrl);
      setScreen("result");
    } catch (err) {
      setError(err.message);
      setScreen("pick");
    }
  };

  const handleCheckout = async (plan) => {
    try {
      const res = await fetch(API_URL + "/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan, userId }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (err) {
      alert("Payment setup failed. Please try again.");
    }
  };

  const reset = () => {
    setScreen("home");
    setSelectedFile(null);
    setPreviewUrl(null);
    setSelectedStyle(null);
    setResultImageUrl(null);
    setError(null);
  };

  const style = STYLES.find((s) => s.id === selectedStyle);

  return (
    <div style={{ minHeight: "100vh", background: "#0a0a0f", fontFamily: "Georgia, serif", color: "#f0ece0" }}>
      <div style={{ maxWidth: 600, margin: "0 auto", padding: "0 20px 80px" }}>
        <div style={{ textAlign: "center", paddingTop: 48, paddingBottom: 16 }}>
          <h1 style={{ fontSize: 72, fontWeight: 900, lineHeight: 1, margin: "0 0 6px", background: "linear-gradient(135deg, #fff8f0 0%, #ffd0a0 50%, #ff8c3c 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            SENTIC <span style={{ fontStyle: "italic" }}>LABS</span>
          </h1>
          <p style={{ color: "#806858", fontSize: 15, margin: 0 }}>See yourself through art history</p>
        </div>

        {screen === "home" && (
          <div>
            <div onDrop={handleDrop} onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onClick={() => fileRef.current.click()}
              style={{ border: "2px dashed " + (dragOver ? "#ff8c3c" : "#302820"), borderRadius: 24, padding: "64px 30px", textAlign: "center", cursor: "pointer", background: dragOver ? "rgba(255,140,60,0.06)" : "rgba(255,255,255,0.015)", marginBottom: 24 }}>
              <div style={{ fontSize: 56, marginBottom: 14 }}>📷</div>
              <p style={{ fontSize: 21, fontWeight: 700, margin: "0 0 6px" }}>Drop your photo here</p>
              <p style={{ color: "#604838", fontSize: 14, margin: "0 0 22px" }}>or click to browse</p>
              <div style={{ display: "inline-block", padding: "13px 32px", background: "linear-gradient(135deg, #ff8c3c, #ff4080)", borderRadius: 50, fontSize: 15, fontWeight: 700, color: "#fff" }}>Choose a Photo</div>
              <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => handleFile(e.target.files[0])} />
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
              {STYLES.map((s) => (
                <span key={s.id} style={{ padding: "6px 14px", borderRadius: 50, background: "rgba(255,255,255,0.04)", border: "1px solid #252018", fontSize: 12, color: "#907060" }}>
                  {s.emoji} {s.label}
                </span>
              ))}
            </div>
          </div>
        )}

        {screen === "pick" && (
          <div>
            {error && <div style={{ background: "rgba(255,60,60,0.1)", border: "1px solid rgba(255,60,60,0.2)", borderRadius: 12, padding: "12px 16px", marginBottom: 16, color: "#ff8080", fontSize: 14 }}>Error: {error}</div>}
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <img src={previewUrl} alt="preview" style={{ width: 110, height: 110, borderRadius: "50%", objectFit: "cover", border: "3px solid #ff8c3c" }} />
              <p style={{ color: "#907060", fontSize: 14, marginTop: 10 }}>First transform is FREE</p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
              {STYLES.map((s) => (
                <div key={s.id} onClick={() => setSelectedStyle(s.id)}
                  style={{ padding: "16px 14px", borderRadius: 16, cursor: "pointer", border: "2px solid " + (selectedStyle === s.id ? "#ff8c3c" : "#201810"), background: selectedStyle === s.id ? "rgba(255,140,60,0.08)" : "rgba(255,255,255,0.015)", textAlign: "center" }}>
                  <div style={{ fontSize: 26, marginBottom: 5 }}>{s.emoji}</div>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{s.label}</div>
                  <div style={{ fontSize: 11, color: "#604838" }}>{s.desc}</div>
                </div>
              ))}
            </div>
            <button onClick={handleTransform} disabled={!selectedStyle}
              style={{ width: "100%", padding: "17px", borderRadius: 50, border: "none", background: selectedStyle ? "linear-gradient(135deg, #ff8c3c, #ff4080)" : "#1a1410", color: selectedStyle ? "#fff" : "#403028", fontSize: 16, fontWeight: 900, cursor: selectedStyle ? "pointer" : "not-allowed", marginBottom: 10 }}>
              {selectedStyle ? "Transform into " + (style ? style.label : "") : "Select a style above"}
            </button>
            <button onClick={reset} style={{ width: "100%", padding: "12px", background: "transparent", border: "1px solid #201810", borderRadius: 50, color: "#604838", fontSize: 14, cursor: "pointer" }}>Back</button>
          </div>
        )}

        {screen === "processing" && (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <div style={{ fontSize: 60, marginBottom: 24 }}>{style ? style.emoji : "🎨"}</div>
            <h2 style={{ fontSize: 26, fontWeight: 900, marginBottom: 10 }}>Creating your {style ? style.label : ""}...</h2>
            <p style={{ color: "#907060", fontSize: 15 }}>This takes about 20-30 seconds</p>
          </div>
        )}

        {screen === "result" && (
          <div>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ display: "inline-block", padding: "6px 18px", borderRadius: 50, background: "rgba(255,140,60,0.12)", border: "1px solid rgba(255,140,60,0.25)", fontSize: 12, color: "#ff8c3c" }}>
                {style ? style.emoji : ""} Your {style ? style.label : ""} Transformation
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "#604838", marginBottom: 8 }}>Before</div>
                <img src={previewUrl} alt="original" style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 16, border: "2px solid #201810" }} />
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "#ff8c3c", marginBottom: 8 }}>After</div>
                {resultImageUrl && <img src={resultImageUrl} alt="result" style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 16, border: "2px solid rgba(255,140,60,0.4)" }} />}
              </div>
            </div>
            <button onClick={() => setScreen("paywall")}
              style={{ width: "100%", padding: "17px", borderRadius: 50, border: "none", background: "linear-gradient(135deg, #ff8c3c, #ff4080)", color: "#fff", fontSize: 16, fontWeight: 900, cursor: "pointer", marginBottom: 10 }}>
              Unlock All 8 Styles - $4.99
            </button>
            <button onClick={reset} style={{ width: "100%", padding: "12px", background: "transparent", border: "1px solid #201810", borderRadius: 50, color: "#604838", fontSize: 14, cursor: "pointer" }}>Start over</button>
          </div>
        )}

        {screen === "paywall" && (
          <div>
            <div style={{ textAlign: "center", marginBottom: 30 }}>
              <div style={{ fontSize: 52, marginBottom: 14 }}>🔓</div>
              <h2 style={{ fontSize: 32, fontWeight: 900, margin: "0 0 8px" }}>Unlock All Styles</h2>
              <p style={{ color: "#907060", fontSize: 15 }}>Ready to see every version of yourself?</p>
            </div>
            <div style={{ padding: "22px", borderRadius: 20, marginBottom: 12, border: "2px solid #ff8c3c", background: "rgba(255,140,60,0.06)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 17 }}>Monthly Unlimited</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#ff8c3c" }}>$7.99/mo</div>
              </div>
              <div style={{ color: "#604838", fontSize: 13, marginBottom: 16 }}>Unlimited transforms, all 8 styles, cancel anytime</div>
              <button onClick={() => handleCheckout("monthly")}
                style={{ width: "100%", padding: "13px", borderRadius: 50, border: "none", background: "linear-gradient(135deg, #ff8c3c, #ff4080)", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
                Get Unlimited
              </button>
            </div>
            <div style={{ padding: "22px", borderRadius: 20, marginBottom: 12, border: "2px solid #201810", background: "rgba(255,255,255,0.015)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 17 }}>One-Time Pack</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#ff8c3c" }}>$4.99</div>
              </div>
              <div style={{ color: "#604838", fontSize: 13, marginBottom: 16 }}>40 transforms, all 8 styles, no subscription</div>
              <button onClick={() => handleCheckout("pack")}
                style={{ width: "100%", padding: "13px", borderRadius: 50, border: "none", background: "rgba(255,255,255,0.05)", color: "#f0ece0", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
                Buy Pack
              </button>
            </div>
            <button onClick={reset} style={{ width: "100%", padding: "10px", background: "transparent", border: "none", color: "#504030", fontSize: 13, cursor: "pointer" }}>Back to start</button>
          </div>
        )}
      </div>
    </div>
  );
}
